import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import { logInfo } from 'utils/log'
import { LoadingMode } from 'components/LoadingProvider'
import { scrollToTop } from 'utils/pollard/scroll'
import { updateFfgConf } from 'startup/command/initFfgConf'
import { getPageNameFromPath } from 'utils/pages'
import { action, makeObservable, observable, reaction } from 'mobx'
import Bugsnag from '@bugsnag/js'
import { AxiosError } from 'axios'
import { RootObject } from 'interfaces/ApolloResponse.interface'
import {
  AmplitudeEvent,
  FinalRedirectStatus
} from 'utils/tracking/amplitude/amplitude.interface'
import { HttpStatusCode } from 'utils/url/status'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import FinalizeError from 'error/finalizeError'
import { isAxiosError } from 'api/utils'
import { trackLead, trackTeal, trackWithOptimizely } from 'utils/tracking'
import RootStore from 'stores/RootStore'
import { getTrackingPath, isTenantSmava } from 'utils/url'
import { ReactSDKClient } from '@optimizely/react-sdk'
import pollard from 'utils/pollard'
import {
  COOKIE_KEY_FCID,
  COOKIE_KEY_FORM_CONFIG,
  setCookie
} from 'utils/cookie'
import { AFFILIATE_ID_SMAVA } from 'utils/affiliate'
import { TEAL_EXPERIMENT_NAME } from 'OptimizelyProvider'
import { getStorage } from 'utils/storage'

export default class Navigation {
  private rootStore: RootStore

  public stack: string[] = []
  public currentPageInSession: string | null = null
  public currentConfigInSession: string | null = null
  // The page, that the customer has seen first, when entering Taurine
  public initiallySeenPage: string | null = null
  public isExistingUser?: boolean
  public tenantContext?: string
  public parentUrl = ''
  public emailFieldDisabled?: boolean

  constructor(rootStore: RootStore) {
    makeObservable<Navigation>(this, {
      stack: observable,
      handleFinalizeErrors: action,
      currentPageInSession: observable,
      currentConfigInSession: observable,
      initiallySeenPage: observable,
      setCurrentConfigInSession: action,
      setCurrentPageInSession: action,
      setInitiallySeenPage: observable,
      parentUrl: observable,
      setParentUrl: action
    })

    this.rootStore = rootStore
    this.parentUrl = ''

    this.rootStore.history.listen((location) => {
      const { setNavigation, config } = this.rootStore.page

      if (!config.version) {
        return
      }

      const pageFromPath = getPageNameFromPath(location.pathname)
      if (!config.pages[pageFromPath]) {
        return
      }
      this.setCurrentPageInSession(pageFromPath)
      setNavigation(pageFromPath)
      this.pushToStack(pageFromPath)
    })

    /**
     * Every time the traversal data changes,
     * it might affect conditions that are in the config,
     * e.g. debtors.relationship === 'SPOUSE' -> `/civilStatus` will not be rendered.
     * Therefore, if the next page was `/civilStatus` it needs to be updated.
     */
    reaction(
      () => this.rootStore.traversal.data,
      () => {
        if (!this.currentPageInSession) {
          return
        }

        this.rootStore.page.setNavigation(this.currentPageInSession)
      }
    )
  }

  static create(rootStore: RootStore): Navigation {
    return new Navigation(rootStore)
  }

  private pushToStack = (fromPage: string) => {
    this.stack = [...this.stack, fromPage]

    Bugsnag.addMetadata('navigation', {
      stack: this.stack
    })
  }

  setCurrentConfigInSession = (configName: string | null) => {
    this.currentConfigInSession = configName
    getStorage('sessionStorage').setItem('configName', configName || '')

    /* set fromConfig from cookie */
    if (configName && isTenantSmava()) {
      setCookie(COOKIE_KEY_FORM_CONFIG, configName)
    }

    this.broadcastNavigationStateToParent({
      currentConfig: configName
    })
  }

  setParentUrl = async () => {
    const data = await pollard.request(
      {
        eventName: RegisteredEventName.getParentFrameUrl,
        data: {
          requestLatestTraversal: true
        }
      },
      5000
    )
    this.parentUrl = data as string
  }

  setEmailFieldDisabled = async () => {
    const data = await pollard.request(
      {
        eventName: RegisteredEventName.getIsEmailFieldDisabled
      },
      5000
    )
    this.emailFieldDisabled = data as boolean
  }

  setCurrentPageInSession = (pageName: string | null) => {
    this.currentPageInSession = pageName

    this.broadcastNavigationStateToParent({
      currentPageName: pageName
    })
  }

  setInitiallySeenPage = (initialPage: string | null) => {
    this.initiallySeenPage = initialPage

    this.broadcastNavigationStateToParent({
      initiallySeenPage: initialPage
    })
  }

  setTenantContext = async () => {
    const data = await pollard.request(
      {
        eventName: RegisteredEventName.getTenantContext
      },
      5000
    )
    this.tenantContext = data as string
  }

  getPreviousNavigation = () =>
    this.stack.length > 1 ? this.stack[this.stack.length - 2] : null

  private broadcastNavigationStateToParent = (
    data: Record<string, unknown>
  ) => {
    broadcastToParent({
      eventName: RegisteredEventName.setCurrentNavigation,
      data: {
        system: 'taurine',
        url: window.location.origin + window.location.pathname,
        currentConfig: this.currentConfigInSession,
        currentPageName: this.currentPageInSession,
        initiallySeenPage: this.initiallySeenPage,
        ...data
      }
    })
  }

  private shouldNavigateToBackUrl = () => {
    const {
      isInIframe,
      page: { getCurrentIndex },
      urlParams: { backUrl }
    } = this.rootStore

    if (!isInIframe || !backUrl) {
      return false
    }

    if (getCurrentIndex() === 0) {
      return true
    }

    return this.currentPageInSession === this.initiallySeenPage
  }

  goToPreviousPage = () => {
    const {
      setLoadingMode,
      page: { removeErrorsForNextPage, previousPageName }
    } = this.rootStore

    trackInAmplitude(AmplitudeEvent.BackButtonIsClicked)

    if (this.shouldNavigateToBackUrl()) {
      logInfo('Navigating to "backUrl"..')

      broadcastToParent({
        eventName: RegisteredEventName.backLink,
        data: null
      })

      // in case the pollard communcation doesn't work
      // TODO: We can use a Pollard request here to ensure a response :)
      setTimeout(() => setLoadingMode(LoadingMode.None), 5000)

      return
    }

    if (!previousPageName) {
      setLoadingMode(LoadingMode.None)

      return
    }

    this.rootStore.history.push(previousPageName)

    this.scrollToTop()
    removeErrorsForNextPage()

    updateFfgConf('step', previousPageName)

    setLoadingMode(LoadingMode.None)
  }

  handleFinalizeErrors = (
    error: Error | AxiosError<RootObject> | undefined
  ) => {
    if (!isAxiosError(error)) {
      throw error
    }

    const amplitudeEvent = { status: FinalRedirectStatus.Failed }

    if (error.response?.status === HttpStatusCode.BadRequest) {
      this.rootStore.fieldErrors.setFieldErrorsFromAxiosResponse(error.response)

      trackInAmplitude(AmplitudeEvent.GoToFinal, {
        ...amplitudeEvent,
        isValidationError: true
      })

      return
    }

    trackInAmplitude(AmplitudeEvent.GoToFinal, {
      ...amplitudeEvent,
      isValidationError: false
    })

    logAndTrackError(
      new FinalizeError('Finalization failed', error.config, error.response)
    )
  }

  validateCSO = async (optimizely: ReactSDKClient) => {
    const {
      setLoadingMode,
      traversal: {
        checkTenantForEmail,
        data: {
          debtors: {
            primary: {
              contact: { email },
              personId
            }
          },
          system: { subId, refId, configurationId }
        }
      }
    } = this.rootStore
    let userSystem = ''
    if (this.currentPageInSession === 'contact' && isTenantSmava() && email) {
      const {
        result: { system, isUserExisting, isMigrated }
      } = await checkTenantForEmail(email.toLocaleLowerCase() as string)

      userSystem = system
      this.setIfExistingUser(isUserExisting)

      setCookie('customerType', isUserExisting ? 'returning' : 'new')

      if (system === 'GREEN') {
        trackTeal({
          transaction: {
            event: 'redirection_shown|registration_route',
            label: 'teal',
            category: 'redirection_shown'
          },
          affiliateId: AFFILIATE_ID_SMAVA,
          subId,
          refId,
          configurationId,
          traversalPersonId: personId
        })
        setCookie('redirectedFrom', 'teal')
        if (!isMigrated) {
          trackTeal({
            transaction: {
              event: 'not_migrated_to_teal',
              label: getTrackingPath(),
              category: 'instant_migration'
            },
            traversalPersonId: personId
          })
          await trackInAmplitude(AmplitudeEvent.OnTealMigrationFailed)
        }

        trackWithOptimizely('redirection_shown', this.rootStore, {
          userFlow: 'Redirected'
        })

        broadcastToParent({
          eventName: RegisteredEventName.deleteCookie,
          data: {
            cookieName: COOKIE_KEY_FCID
          }
        })

        broadcastToParent({
          eventName: RegisteredEventName.setParentStorageData,
          data: {
            storage: 'localStorage',
            key: 'IMOD_CURRENT_TRAVERSAL_DATA',
            value: {}
          }
        })

        broadcastToParent({
          eventName: RegisteredEventName.setParentStorageData,
          data: {
            storage: 'sessionStorage',
            key: 'IMOD_CURRENT_NAVIGATION',
            value: {}
          }
        })

        optimizely.setForcedVariation(TEAL_EXPERIMENT_NAME, 'variation_1')

        await trackInAmplitude(AmplitudeEvent.OnUserRedirectedToGreen)

        broadcastToParent({
          eventName: RegisteredEventName.redirect,
          data: {
            url: 'https://www.smava.de/sicheres-einloggen-mit-zugangsdaten?redirected&type=loginWeiterleitung'
          }
        })
        setLoadingMode(LoadingMode.None)
      }
      if (isMigrated) {
        trackTeal({
          transaction: {
            event: 'instantly_migrated_to_teal',
            label: getTrackingPath(),
            category: 'instant_migration'
          },
          traversalPersonId: personId
        })
        await trackInAmplitude(AmplitudeEvent.OnTealMigrationInitiated)
      }
    }
    return userSystem === 'GREEN'
  }

  goToNextPage = async () => {
    const {
      setLoadingMode,
      page: { nextPageName, isOnTermsAndConditionsPage },
      traversal: { finalize, hasAcceptedTermsAndConditions }
    } = this.rootStore

    if (isTenantSmava()) {
      switch (this.currentPageInSession) {
        case 'contact': {
          trackWithOptimizely('form_submission.contact_info', this.rootStore)
          break
        }
        case 'address': {
          trackWithOptimizely('form_submission.address', this.rootStore)
          break
        }
        case 'household_employment':
        case 'employment': {
          trackWithOptimizely('form_submission.employment', this.rootStore)
          break
        }
        case 'income_expenses':
        case 'income': {
          trackWithOptimizely('form_submission.income_expenses', this.rootStore)
          break
        }
        case 'loanPayout':
        case 'rdi': {
          trackWithOptimizely('form_submission.rdi_account', this.rootStore)
          break
        }
        default: {
          break
        }
      }
    }

    if (!nextPageName) {
      await finalize(this.handleFinalizeErrors)
      return
    }
    if (
      hasAcceptedTermsAndConditions() &&
      (await isOnTermsAndConditionsPage())
    ) {
      trackLead(this.rootStore)
      if (isTenantSmava()) {
        trackWithOptimizely('all_lead', this.rootStore)
      }
    }

    this.scrollToTop()

    updateFfgConf('step', nextPageName)

    this.rootStore.history.push(nextPageName)

    setLoadingMode(LoadingMode.None)
  }

  setIfExistingUser = (isUserExisting: boolean) => {
    this.isExistingUser = isUserExisting
  }

  scrollToTop = () => scrollToTop(this.rootStore.isInIframe)
}
